import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GalleryItem } from "@/data/projects";

interface MediaLightboxProps {
  items: GalleryItem[];
  activeIndex: number | null;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

export function MediaLightbox({ items, activeIndex, onClose, onNavigate }: MediaLightboxProps) {
  const isOpen = activeIndex !== null && !!items[activeIndex];
  const item = isOpen ? items[activeIndex as number] : null;

  return (
    <AnimatePresence>
      {isOpen && item && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 md:p-10"
          onClick={onClose}
        >
          <button
            onClick={onClose}
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>

          {items.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onNavigate(((activeIndex as number) - 1 + items.length) % items.length);
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
                aria-label="Previous"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onNavigate(((activeIndex as number) + 1) % items.length);
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
                aria-label="Next"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}

          <motion.div
            key={activeIndex}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            className="max-h-full max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            {item.type === "video" ? (
              <video src={item.url} controls autoPlay className="max-h-[80vh] w-full rounded-lg" />
            ) : (
              <img
                src={item.url}
                alt={item.caption || ""}
                className="max-h-[80vh] w-full rounded-lg object-contain"
              />
            )}
            {item.caption && <p className="mt-3 text-center text-sm text-white/70">{item.caption}</p>}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}